import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import {
  LARGE_CATEGORY_PAGE_PATH,
  MIDDLE_CATEGORY_PAGE_PATH,
  PRODUCT_PAGE_PATH,
  SMALL_CATEGORY_PAGE_PATH,
  TINY_CATEGORY_PAGE_PATH
} from 'src/constants/pagePath';
import { fetchProductDetailByIdInClient } from 'src/libs/api/productsApi';
import type { ProductDetail } from 'src/models/product/schema';
import { useBrands } from './useBrands';
import { useCategory } from './useCategory';

export const useCategoryBrands = () => {
  const { category } = useCategory();
  const { brands } = useBrands();
  const { query, pathname } = useRouter();
  const slug = typeof query.slug === 'string' ? query.slug : '';
  const productId = Number(query?.id);
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const res = await fetchProductDetailByIdInClient(productId);
      if (res.isRight()) {
        setProductDetail(res.value);
      }
    };
    if (productId) {
      fetchData();
    }
  }, [productId]);

  const getProductParentCategory = () => {
    if (!productDetail) return null;

    const { largeCategoryId, middleCategoryId, smallCategoryId, tinyCategoryId } = productDetail;

    return (
      category?.tinyCategories.find((tc) => tc.tinyCategoryId === tinyCategoryId) ||
      category?.smallCategories.find((sc) => sc.smallCategoryId === smallCategoryId) ||
      category?.middleCategories.find((mc) => mc.middleCategoryId === middleCategoryId) ||
      category?.largeCategories.find((lc) => lc.largeCategoryId === largeCategoryId)
    );
  };

  const getProductBrandList = () => {
    if (!productDetail) return [];

    const { largeCategoryId, middleCategoryId, smallCategoryId, tinyCategoryId } = productDetail;

    if (tinyCategoryId)
      return brands?.filter((brand) => brand.tinyCategories.some((btc) => btc.id === tinyCategoryId)) || [];
    if (smallCategoryId)
      return brands?.filter((brand) => brand.smallCategories.some((bsc) => bsc.id === smallCategoryId)) || [];
    if (middleCategoryId)
      return brands?.filter((brand) => brand.middleCategories.some((bmc) => bmc.id === middleCategoryId)) || [];
    return brands?.filter((brand) => brand.largeCategories.some((blc) => blc.id === largeCategoryId)) || [];
  };

  const categoryWithBrands = useMemo(() => {
    // 商品詳細
    if (pathname === PRODUCT_PAGE_PATH && productDetail) {
      const parentCategory = getProductParentCategory();
      const brands = getProductBrandList();

      return {
        parentCategory,
        subCategories: [
          {
            slug: parentCategory?.slug || slug,
            name: parentCategory?.name || '',
            brands
          }
        ],
        hasSubCategories: false
      };
      // 大カテ
    } else if (pathname === LARGE_CATEGORY_PAGE_PATH) {
      return {
        parentCategory: category?.largeCategories.find((lc) => lc.slug === slug),
        subCategories:
          category?.middleCategories
            .filter((mc) =>
              category.largeCategories.some(
                (lc) => lc.slug === slug && lc.middleCategoryIds.includes(mc.middleCategoryId)
              )
            )
            .map((mc) => ({
              slug: mc.slug,
              name: mc.name,
              brands:
                brands?.filter((brand) => brand.middleCategories.some((bmc) => bmc.id === mc.middleCategoryId)) || []
            })) ?? [],
        hasSubCategories: true
      };
      // 中カテ
    } else if (pathname === MIDDLE_CATEGORY_PAGE_PATH) {
      const parentCategory = category?.middleCategories.find((mc) => mc.slug === slug);
      const subCategories =
        category?.smallCategories
          .filter((sc) =>
            category.middleCategories.some((mc) => mc.slug === slug && mc.smallCategoryIds.includes(sc.smallCategoryId))
          )
          .map((sc) => ({
            slug: sc.slug,
            name: sc.name,
            brands: brands?.filter((brand) => brand.smallCategories.some((bsc) => bsc.id === sc.smallCategoryId)) || []
          })) ?? [];
      const hasSubCategories = subCategories.length > 0;

      return {
        parentCategory,
        subCategories: hasSubCategories
          ? subCategories
          : [
              {
                slug: parentCategory?.slug || slug,
                name: parentCategory?.name || '',
                brands: brands?.filter((brand) => brand.middleCategories.some((bmc) => bmc.slug === slug)) || []
              }
            ],
        hasSubCategories
      };
      // 小カテ
    } else if (pathname === SMALL_CATEGORY_PAGE_PATH) {
      const parentCategory = category?.smallCategories.find((sc) => sc.slug === slug);
      const subCategories =
        category?.tinyCategories
          .filter((tc) =>
            category.smallCategories.some((sc) => sc.slug === slug && sc.tinyCategoryIds.includes(tc.tinyCategoryId))
          )
          .map((tc) => ({
            slug: tc.slug,
            name: tc.name,
            brands: brands?.filter((brand) => brand.tinyCategories.some((btc) => btc.id === tc.tinyCategoryId)) || []
          })) ?? [];
      const hasSubCategories = subCategories.length > 0;

      return {
        parentCategory,
        subCategories: hasSubCategories
          ? subCategories
          : [
              {
                slug: parentCategory?.slug || slug,
                name: parentCategory?.name || '',
                brands: brands?.filter((brand) => brand.smallCategories.some((bsc) => bsc.slug === slug)) || []
              }
            ],
        hasSubCategories
      };
      // 極小カテ
    } else if (pathname === TINY_CATEGORY_PAGE_PATH) {
      const parentCategory = category?.tinyCategories.find((tc) => tc.slug === slug);
      return {
        parentCategory,
        subCategories: [
          {
            slug: parentCategory?.slug || slug,
            name: parentCategory?.name || '',
            brands: brands?.filter((brand) => brand.tinyCategories.some((btc) => btc.slug === slug)) || []
          }
        ],
        hasSubCategories: false
      };
    }
    return { parentCategory: undefined, subCategories: [], hasSubCategories: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, brands, slug, pathname, productDetail]);

  return { ...categoryWithBrands };
};
